/**
 * Mode-aware bot objective scoring.
 * Pure functions — no Three.js, no side effects, deterministic given same inputs.
 *
 * Provides score adjustments for mode-specific actions (plant stake, collect, deliver, etc.)
 * These scores are merged into the main chooseBotAction scoring in bot.ts.
 */

import { canPlantOnPlanet } from '../core/stakes';
import { nearestSurfaceVertex } from '../core/surface';
import { BOT_HONEST_OBJECTIVES, PLANT_FLAG_MOVEMENT_COST } from '../constants';
import type {
  PlayerState,
  PlanetState,
  GameState,
  RuinState,
  CollectibleNode,
  CarryableObjectState,
  Vec3,
  ModeConfig,
  BotFogContext,
} from '../types';
import { vecSub, vecLength, vecNormalize, vecScale, vecAdd, vecDot } from '../math';
import {
  CLAIM_RANGE,
  CLAIM_MOVEMENT_COST,
  CARRYABLE_PICKUP_RANGE,
} from '../constants';
import { canPickup, getPlayerCarrying } from '../core/carryables';
import { getModeRules } from '../core/modeRules';
import { getPlayerWorldPosition, getRuinWorldPosition, arcDistance } from '../core/gameState';
import { canInteract } from '../core/interactions';
import { canBuildDrill } from '../core/excavation';
import type { SeededRandom } from '../core/random';

// =============================================================================
// TYPES
// =============================================================================

/** Extended bot action type including mode-specific actions */
export type BotModeAction =
  | 'plantStake' | 'removeStake' | 'interact'
  | 'collect' | 'deliver' | 'buildDrill'
  | 'pickup' | 'claim';

// =============================================================================
// HELPERS
// =============================================================================

// Terrain-exact positions (shared with the rest of the game; these used to be
// base-radius copies that ignored the heightmap)
function getPlayerWorldPos(player: PlayerState, planets: PlanetState[]): Vec3 {
  return getPlayerWorldPosition(player, planets);
}

function getRuinWorldPos(ruin: RuinState, planets: PlanetState[]): Vec3 {
  return getRuinWorldPosition(ruin, planets);
}

type Personality = { aggression: number; mobility: number; riskTolerance: number };

/** Live (possibly adaptive) personality passed by the caller wins over the static botConfig. */
function getPersonality(player: PlayerState, override?: Personality): Personality {
  if (override) return override;
  return (player as unknown as { botConfig?: { personality: Personality } })
    .botConfig?.personality ?? { aggression: 0.5, mobility: 0.5, riskTolerance: 0.5 };
}

// =============================================================================
// MAIN SCORER
// =============================================================================

/**
 * Score mode-specific actions for a bot.
 * Returns a partial record of action → score adjustments.
 * Only non-zero scores are included.
 */
export function scoreModeActions(
  player: PlayerState,
  gameState: GameState,
  config: ModeConfig,
  rng: SeededRandom,
  fogContext?: BotFogContext,
  personalityOverride?: Personality,
): Partial<Record<BotModeAction, number>> {
  const scores: Partial<Record<BotModeAction, number>> = {};
  const objective = getModeRules(config).objective;

  if (objective === 'none') return scores;

  const personality = getPersonality(player, personalityOverride);

  switch (objective) {
    case 'stakes': {
      if (canBotPlantStake(player, gameState)) {
        scores.plantStake = 0.6 + 0.3 * personality.aggression;
      }
      const removeTarget = canBotRemoveStake(player, gameState);
      if (removeTarget) {
        scores.removeStake = 0.5 + 0.4 * personality.aggression;
      }
      break;
    }
    case 'ctf': {
      const pickupTarget = canBotPickup(player, gameState);
      if (pickupTarget) {
        scores.pickup = 0.9;
      }
      break;
    }
    case 'signalTrace': {
      const interactTarget = canBotInteract(player, gameState);
      if (interactTarget) {
        scores.interact = 0.7 + 0.2 * (1 - personality.aggression);
      }
      break;
    }
    case 'uplink': {
      const collectTarget = canBotCollect(player, gameState);
      if (collectTarget) {
        scores.collect = 0.6;
      }
      const deliverTarget = canBotDeliver(player, gameState);
      if (deliverTarget) {
        scores.deliver = 0.8;
      }
      break;
    }
    case 'salvage': {
      const collectTarget = canBotCollect(player, gameState);
      if (collectTarget) {
        const valueBonus = collectTarget.value ? collectTarget.value / 25 : 0;
        scores.collect = 0.5 + 0.2 * valueBonus;
      }
      const deliverTarget = canBotDeliver(player, gameState);
      if (deliverTarget) {
        scores.deliver = 0.7;
      }
      break;
    }
    case 'excavation': {
      if (canBotBuildDrill(player, gameState)) {
        scores.buildDrill = 0.8;
      }
      const pickupTarget = canBotPickup(player, gameState);
      if (pickupTarget) {
        scores.pickup = 0.9;
      }
      break;
    }
  }

  // --- All objectives: claim ruins ---
  const claimTarget = canBotClaim(player, gameState);
  if (claimTarget) {
    scores.claim = 0.3 + 0.2 * (1 - personality.aggression);
  }

  return scores;
}

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Check if bot can plant a stake on the current planet.
 */
export function canBotPlantStake(player: PlayerState, gameState: GameState): boolean {
  if (!player.alive || player.airborne) return false;
  if (player.flagsRemaining <= 0) return false;
  if (player.movementRemaining < PLANT_FLAG_MOVEMENT_COST) return false;   // the game's own cost (was a guessed 0.5)

  // Check if this planet already has a stake from this team
  const config = gameState.config.modeConfig;
  if (!config.multiStake) {
    const hasStake = gameState.plantedFlags.some(
      f => f.teamIndex === player.teamIndex
        && f.planetIndex === player.planetIndex
        && (f.alive !== false)
    );
    if (hasStake) return false;
  }
  // the game's own rule for the planet (single-stake modes: not where the enemy holds a stake) — the bot planned a
  // stake there and was refused every time (the every-switch check: 39 refusals to 8 stakes in three dominion matches)
  if (!canPlantOnPlanet(player, player.planetIndex, gameState.plantedFlags, config)) return false;

  return true;
}

/**
 * Check if bot can remove an enemy stake nearby.
 * Returns the stake info or null.
 */
export function canBotRemoveStake(
  player: PlayerState,
  gameState: GameState,
): { id: number; surfaceDir: Vec3 } | null {
  if (!player.alive || player.airborne) return null;

  const removeCost = gameState.config.modeConfig.stakeRemoveCost ?? 1.0;
  if (player.movementRemaining < removeCost) return null;

  // Find enemy stake on same planet
  for (const flag of gameState.plantedFlags) {
    if (flag.teamIndex === player.teamIndex) continue;
    if (flag.planetIndex !== player.planetIndex) continue;
    if (flag.alive === false) continue;

    return { id: flag.id, surfaceDir: flag.surfaceDir };
  }

  return null;
}

/**
 * Check if bot can interact with a ruin (Signal Trace listening posts, etc.)
 */
export function canBotInteract(
  player: PlayerState,
  gameState: GameState,
): RuinState | null {
  if (!player.alive || player.airborne) return null;
  if (player.movementRemaining < 0.5) return null;

  // In range only: an interaction started from across the planet is cancelled
  // at the round wrap (checkInteractionInterruptions), so the bot must stand
  // next to the ruin — getBotSamePlanetObjectiveDir walks it there first.
  for (const ruin of gameState.ruins) {
    if (!ruin.interactable) continue;
    if (ruin.planetIndex !== player.planetIndex) continue;
    if (!canInteract(player, ruin, gameState.planets)) continue;
    if (!ruinWorthInteracting(player, gameState, ruin)) continue;
    return ruin;
  }

  return null;
}

/**
 * Signal Trace: the team already has an activated post on this planet.
 * Triangulation counts distinct planets, so a second post here adds nothing.
 */
function teamHasPostOnPlanet(gameState: GameState, teamIndex: number, planetIndex: number): boolean {
  const st = gameState.modeState.signalTrace;
  if (!st) return false;
  const activated = st.clueState.activatedPosts[teamIndex] ?? [];
  return activated.some(id => gameState.ruins.find(r => r.id === id)?.planetIndex === planetIndex);
}

/** A ruin this team has not already worked, is not already working on, and that still helps the objective. */
function ruinWorthInteracting(player: PlayerState, gameState: GameState, ruin: RuinState): boolean {
  const inProgress = (gameState.modeState.interactionProgress ?? []).some(
    ip => ip.ruinId === ruin.id && ip.teamIndex === player.teamIndex,
  );
  if (inProgress) return false;
  const st = gameState.modeState.signalTrace;
  if (st) {
    if ((st.clueState.activatedPosts[player.teamIndex] ?? []).includes(ruin.id)) return false;
    if (teamHasPostOnPlanet(gameState, player.teamIndex, ruin.planetIndex)) return false;
  }
  return true;
}

/**
 * The ruin this bot is currently working on, on its own planet, if any.
 * A bot holding an interaction must stay in range until it completes.
 */
export function getBotInteractionInProgress(player: PlayerState, gameState: GameState): RuinState | null {
  const progress = gameState.modeState.interactionProgress;
  if (!progress) return null;
  for (const ip of progress) {
    if (ip.playerId !== player.id) continue;
    const ruin = gameState.ruins.find(r => r.id === ip.ruinId);
    if (ruin && ruin.planetIndex === player.planetIndex) return ruin;
  }
  return null;
}

/** Nearest ruin on the bot's planet still worth working, in walking terms. */
function nearestWorthwhileRuin(player: PlayerState, gameState: GameState): RuinState | null {
  const planet = gameState.planets[player.planetIndex];
  if (!planet) return null;
  let best: RuinState | null = null;
  let bestDist = Infinity;
  for (const ruin of gameState.ruins) {
    if (!ruin.interactable || ruin.planetIndex !== player.planetIndex) continue;
    if (!ruinWorthInteracting(player, gameState, ruin)) continue;
    const d = arcDistance(player.surfaceDir, ruin.surfaceDir, planet.radius);
    if (d < bestDist) { bestDist = d; best = ruin; }
  }
  return best;
}

/**
 * Check if bot can collect a nearby collectible.
 */
export function canBotCollect(
  player: PlayerState,
  gameState: GameState,
): CollectibleNode | null {
  if (!player.alive || player.airborne) return null;
  if (player.movementRemaining < 0.5) return null;

  for (const c of gameState.collectibles) {
    if (c.collected) continue;
    if (c.planetIndex !== player.planetIndex) continue;
    return c;
  }

  return null;
}

/**
 * Check if bot is carrying something and can deliver it.
 */
export function canBotDeliver(
  player: PlayerState,
  gameState: GameState,
): CarryableObjectState | null {
  if (!player.alive || player.airborne) return null;

  const carried = gameState.carryables.find(
    c => c.carriedBy === player.id && c.alive
  );
  if (!carried) return null;

  const ms = gameState.modeState;

  // Uplink: deliver to antenna ruin on same planet
  if (ms.uplink) {
    const antennaRuin = gameState.ruins.find(r => r.id === ms.uplink!.antennaRuinId);
    if (antennaRuin && antennaRuin.planetIndex === player.planetIndex) {
      return carried;
    }
  }

  // Salvage: deliver to depot planet
  if (ms.salvage) {
    const depotPlanet = ms.salvage.depotPlanets[player.teamIndex];
    if (depotPlanet === player.planetIndex) {
      return carried;
    }
  }

  return null;
}

/**
 * Check if bot can build a drill on the artifact planet (Excavation mode).
 */
export function canBotBuildDrill(
  player: PlayerState,
  gameState: GameState,
): boolean {
  const ex = gameState.modeState.excavation;
  if (!ex) return false;

  // The game's own rule: on the artifact planet, within drill range of the site,
  // with the movement to spend. Out of range, getBotSamePlanetObjectiveDir walks
  // the bot to the dig site first.
  if (!canBuildDrill(player, gameState.planets, ex.artifactPlanetIndex, ex.artifactSurfaceDir)) return false;

  // Check if team already has a drill
  const hasDrill = ex.drillSites.some(
    d => d.teamIndex === player.teamIndex && d.health > 0
  );

  return !hasDrill;
}

/**
 * Check if bot can pick up a carryable on the ground (CTF nav cores, artifact, etc.)
 * Uses actual canPickup which includes proximity check (CARRYABLE_PICKUP_RANGE).
 */
export function canBotPickup(
  player: PlayerState,
  gameState: GameState,
): CarryableObjectState | null {
  if (!player.alive || player.airborne) return null;

  for (const c of gameState.carryables) {
    if (canPickup(player, c, gameState.planets, gameState.carryables)) {
      return c;
    }
  }

  return null;
}

/**
 * Check if bot can claim a nearby ruin.
 */
export function canBotClaim(
  player: PlayerState,
  gameState: GameState,
): RuinState | null {
  if (!player.alive || player.airborne) return null;
  if (player.movementRemaining < CLAIM_MOVEMENT_COST) return null;

  const planets = gameState.planets;
  const playerPos = getPlayerWorldPos(player, planets);

  for (const ruin of gameState.ruins) {
    if (ruin.planetIndex !== player.planetIndex) continue;

    // Skip already claimed by own team with active benefit
    if (ruin.claimTeamIndex !== undefined && ruin.claimTeamIndex >= 0
        && ruin.claimTeamIndex === player.teamIndex && ruin.claimBenefitActive) {
      continue;
    }

    // Skip ruins without useful claim types
    const usefulTypes = ['antenna', 'refinery', 'surveyStation', 'habitat', 'supplyCache', 'commsRelay'];
    if (!usefulTypes.includes(ruin.ruinType)) continue;

    const ruinPos = getRuinWorldPos(ruin, planets);
    const dist = vecLength(vecSub(playerPos, ruinPos));
    if (dist <= CLAIM_RANGE) {
      return ruin;
    }
  }

  return null;
}

/**
 * Get the mode-specific objective target for a bot (planet + surfaceDir to move toward).
 * Returns urgency (0-1) indicating how strongly the bot should prioritize jumping there.
 */
/**
 * K3 (37.20 ledger #4): may this team know an objective object is here — its own, carried, or on ground some
 * teammate has revealed? A human sees a resting carryable or a collectible only on revealed surface. With bot fog
 * off the setting's rule applies: everything is known.
 */
function teamKnowsSpot(gameState: GameState, teamIndex: number, planetIndex: number, surfaceDir: Vec3): boolean {
  if (!gameState.config.botFogEnabled || !BOT_HONEST_OBJECTIVES) return true;
  const teamHasFog = gameState.players.some((p, i) => p.teamIndex === teamIndex && !!gameState.fog?.[i]);
  if (!teamHasFog) return true;   // no knowledge model in this state (fixtures, tools): everything known
  const planet = gameState.planets[planetIndex];
  if (!planet) return false;
  const v = nearestSurfaceVertex(planet, surfaceDir);
  let tracked = false;   // a state with no fog for this team (fixtures, tools) has no knowledge model: everything known
  for (let i = 0; i < gameState.players.length; i++) {
    if (gameState.players[i].teamIndex !== teamIndex) continue;
    const fp = gameState.fog?.[i]?.planets[planetIndex];
    if (!fp) continue;
    tracked = true;
    if (fp.revealedVertices[v]) return true;
  }
  return !tracked;
}

/**
 * K3 follow-up: with an objective known to exist but nowhere the team has seen, the planet the team has revealed
 * least (other than this one) — search where knowledge is thinnest. Null with honest objectives off, without team
 * fog (fixtures), or when every other planet is well seen (above 60 %).
 */
function searchTarget(player: PlayerState, gameState: GameState, urgency: number): { planetIndex: number; surfaceDir: Vec3; urgency: number } | null {
  if (!gameState.config.botFogEnabled || !BOT_HONEST_OBJECTIVES) return null;
  const mates = gameState.players.map((p, i) => ({ p, i })).filter(x => x.p.teamIndex === player.teamIndex && gameState.fog?.[x.i]);
  if (mates.length === 0) return null;
  let best: { planetIndex: number; frac: number } | null = null;
  for (let pi = 0; pi < gameState.planets.length; pi++) {
    const planet = gameState.planets[pi];
    if (pi === player.planetIndex || !planet.alive || planet.type !== 'planet') continue;
    const n = planet.heightmap.length;
    let seen = 0;
    for (let v = 0; v < n; v++) if (mates.some(m => gameState.fog[m.i].planets[pi]?.revealedVertices[v])) seen++;
    const frac = n > 0 ? seen / n : 1;
    if (frac > 0.6) continue;
    if (!best || frac < best.frac) best = { planetIndex: pi, frac };
  }
  return best ? { planetIndex: best.planetIndex, surfaceDir: { x: 0, y: 1, z: 0 }, urgency } : null;
}

export function getBotObjectiveTarget(
  player: PlayerState,
  gameState: GameState,
): { planetIndex: number; surfaceDir: Vec3; urgency: number } | null {
  const ms = gameState.modeState;

  // --- Stakes ---
  if (ms.territory && player.flagsRemaining > 0) {
    // Find nearest unstaked planet (by this team)
    const planets = gameState.planets;
    for (let i = 0; i < planets.length; i++) {
      if (i === player.planetIndex) continue;
      const p = planets[i];
      if (!p.alive || p.type !== 'planet') continue;
      const hasTeamStake = gameState.plantedFlags.some(
        f => f.teamIndex === player.teamIndex && f.planetIndex === i && f.alive !== false
      );
      if (!hasTeamStake) {
        return { planetIndex: i, surfaceDir: { x: 0, y: 1, z: 0 }, urgency: 0.2 };
      }
    }
    return null;
  }

  // --- CTF ---
  if (ms.ctf) {
    // If carrying enemy nav core, go home (high urgency)
    const carrying = gameState.carryables.find(c => c.carriedBy === player.id);
    if (carrying) {
      const homePlanet = gameState.config.modeConfig.homeTeamPlanets?.[player.teamIndex];
      if (homePlanet !== undefined && homePlanet !== player.planetIndex) {
        return { planetIndex: homePlanet, surfaceDir: { x: 0, y: 1, z: 0 }, urgency: 0.6 };
      }
      return null;
    }
    // Not carrying: go grab enemy nav core on ground
    for (const c of gameState.carryables) {
      if (c.type !== 'navCore' || !c.alive || c.carriedBy !== null) continue;
      if (c.teamIndex === player.teamIndex) continue;
      if (c.planetIndex === player.planetIndex) continue;
      // K3: the spot only if the team has seen it; a core at rest on its public home planet is still a destination
      const home = gameState.config.modeConfig.homeTeamPlanets?.[c.teamIndex];
      if (teamKnowsSpot(gameState, player.teamIndex, c.planetIndex, c.surfaceDir)) return { planetIndex: c.planetIndex, surfaceDir: c.surfaceDir, urgency: 0.35 };
      if (home === c.planetIndex) return { planetIndex: c.planetIndex, surfaceDir: { x: 0, y: 1, z: 0 }, urgency: 0.35 };
    }
    return null;
  }

  // --- Signal Trace ---
  if (ms.signalTrace) {
    const st = ms.signalTrace;
    // If signal revealed for team, go extract
    if (st.clueState.signalRevealed[player.teamIndex]) {
      if (st.signalPlanetIndex !== player.planetIndex) {
        return { planetIndex: st.signalPlanetIndex, surfaceDir: st.signalSurfaceDir, urgency: 0.55 };
      }
      return null;
    }
    // Otherwise, find a listening post on another planet the team has not covered yet
    // (triangulation counts distinct planets, so skip planets with a post already)
    const activated = st.clueState.activatedPosts[player.teamIndex] ?? [];
    for (const ruin of gameState.ruins) {
      if (!ruin.interactable) continue;
      if (ruin.planetIndex === player.planetIndex) continue;
      if (activated.includes(ruin.id)) continue;
      if (teamHasPostOnPlanet(gameState, player.teamIndex, ruin.planetIndex)) continue;
      // Check not already in-progress
      const inProgress = (ms.interactionProgress ?? []).some(
        ip => ip.ruinId === ruin.id && ip.teamIndex === player.teamIndex
      );
      if (inProgress) continue;
      return { planetIndex: ruin.planetIndex, surfaceDir: ruin.surfaceDir, urgency: 0.3 };
    }
    return null;
  }

  // --- Uplink ---
  if (ms.uplink) {
    // Carrying fragment: deliver to antenna ruin
    const carrying = gameState.carryables.find(c => c.carriedBy === player.id && c.alive);
    if (carrying) {
      const antennaRuin = gameState.ruins.find(r => r.id === ms.uplink!.antennaRuinId);
      if (antennaRuin && antennaRuin.planetIndex !== player.planetIndex) {
        return { planetIndex: antennaRuin.planetIndex, surfaceDir: antennaRuin.surfaceDir, urgency: 0.5 };
      }
      return null;
    }
    // Not carrying: go collect uncollected fragment on another planet
    for (const c of gameState.collectibles) {
      if (c.collected || c.planetIndex === player.planetIndex) continue;
      if (!teamKnowsSpot(gameState, player.teamIndex, c.planetIndex, c.surfaceDir)) continue;   // K3
      if (c.type === 'dataFragment') {
        return { planetIndex: c.planetIndex, surfaceDir: c.surfaceDir, urgency: 0.25 };
      }
    }
    // K3 follow-up: fragments remain (the count is public) but none is known — go look where the team has seen least
    if (gameState.collectibles.some(c => !c.collected && c.type === 'dataFragment')) return searchTarget(player, gameState, 0.2);
    return null;
  }

  // --- Salvage ---
  if (ms.salvage) {
    // Carrying salvage: deliver to depot
    const carrying = gameState.carryables.find(c => c.carriedBy === player.id && c.alive);
    if (carrying) {
      const depot = ms.salvage.depotPlanets[player.teamIndex];
      if (depot !== undefined && depot !== player.planetIndex) {
        return { planetIndex: depot, surfaceDir: { x: 0, y: 1, z: 0 }, urgency: 0.5 };
      }
      return null;
    }
    // Not carrying: go collect salvage node on another planet
    for (const c of gameState.collectibles) {
      if (c.collected || c.planetIndex === player.planetIndex) continue;
      if (!teamKnowsSpot(gameState, player.teamIndex, c.planetIndex, c.surfaceDir)) continue;   // K3
      if (c.type === 'salvageNode') {
        return { planetIndex: c.planetIndex, surfaceDir: c.surfaceDir, urgency: 0.2 };
      }
    }
    if (gameState.collectibles.some(c => !c.collected && c.type === 'salvageNode')) return searchTarget(player, gameState, 0.15);
    return null;
  }

  // --- Excavation ---
  if (ms.excavation) {
    const ex = ms.excavation;
    // Artifact extracted and carryable on ground on another planet: go grab it
    if (ex.artifactExtracted && ex.artifactCarryableId != null) {
      const artifact = gameState.carryables.find(c => c.id === ex.artifactCarryableId);
      if (artifact && artifact.carriedBy === null && artifact.planetIndex !== player.planetIndex) {
        return { planetIndex: artifact.planetIndex, surfaceDir: artifact.surfaceDir, urgency: 0.6 };
      }
      return null;
    }
    // Not extracted: go to artifact planet
    if (ex.artifactPlanetIndex !== player.planetIndex) {
      return {
        planetIndex: ex.artifactPlanetIndex,
        surfaceDir: ex.artifactSurfaceDir,
        urgency: 0.4,
      };
    }
  }

  return null;
}

/**
 * Get a same-planet objective direction the bot should walk toward.
 * Returns the surfaceDir of the nearest objective on the bot's current planet,
 * or null if no same-planet objectives exist.
 *
 * Used by computeBotMove to prioritize walking toward objectives (core pickup,
 * collectible, delivery point, etc.) over combat repositioning.
 */
export function getBotSamePlanetObjectiveDir(
  player: PlayerState,
  gameState: GameState,
): { surfaceDir: Vec3; type: string } | null {
  const ms = gameState.modeState;

  // Already carrying something? No need to walk to pickup target.
  const carrying = getPlayerCarrying(player.id, gameState.carryables);

  // CTF: walk toward enemy nav core on same planet (if not already carrying)
  if (ms.ctf && !carrying) {
    for (const c of gameState.carryables) {
      if (!c.alive || c.carriedBy !== null) continue;
      if (c.planetIndex !== player.planetIndex) continue;
      if (c.type === 'navCore' && c.teamIndex === player.teamIndex) continue;
      if (!teamKnowsSpot(gameState, player.teamIndex, c.planetIndex, c.surfaceDir)) continue;   // K3
      return { surfaceDir: c.surfaceDir, type: 'navCore' };
    }
  }

  // Uplink/Salvage: walk toward collectible on same planet (if not already carrying)
  if ((ms.uplink || ms.salvage) && !carrying) {
    for (const c of gameState.collectibles) {
      if (c.collected) continue;
      if (c.planetIndex !== player.planetIndex) continue;
      if (!teamKnowsSpot(gameState, player.teamIndex, c.planetIndex, c.surfaceDir)) continue;   // K3
      return { surfaceDir: c.surfaceDir, type: 'collectible' };
    }
  }

  // Uplink: carrying a fragment on the antenna planet — walk to the antenna
  if (ms.uplink && carrying) {
    const antenna = gameState.ruins.find(r => r.id === ms.uplink!.antennaRuinId);
    if (antenna && antenna.planetIndex === player.planetIndex) {
      return { surfaceDir: antenna.surfaceDir, type: 'antenna' };
    }
  }

  // Signal Trace: the revealed source on this planet (stand in the extraction
  // zone), else the nearest listening post still worth working
  if (ms.signalTrace) {
    const st = ms.signalTrace;
    if (st.clueState.signalRevealed[player.teamIndex] && st.signalPlanetIndex === player.planetIndex
        && st.extractionCompletedTeam == null) {
      return { surfaceDir: st.signalSurfaceDir, type: 'signalSource' };
    }
    const working = getBotInteractionInProgress(player, gameState);
    if (working) return { surfaceDir: working.surfaceDir, type: 'post' };
    const post = nearestWorthwhileRuin(player, gameState);
    if (post) return { surfaceDir: post.surfaceDir, type: 'post' };
  }

  // Excavation: walk toward artifact carryable on same planet (if not already
  // carrying), else to the dig site when the team still needs a drill there
  if (ms.excavation && !carrying) {
    const ex = ms.excavation;
    if (ex.artifactExtracted && ex.artifactCarryableId != null) {
      const artifact = gameState.carryables.find(c => c.id === ex.artifactCarryableId);
      if (artifact && artifact.carriedBy === null && artifact.planetIndex === player.planetIndex) {
        return { surfaceDir: artifact.surfaceDir, type: 'artifact' };
      }
    } else if (player.planetIndex === ex.artifactPlanetIndex
        && !ex.drillSites.some(d => d.teamIndex === player.teamIndex && d.health > 0)) {
      return { surfaceDir: ex.artifactSurfaceDir, type: 'digSite' };
    }
  }

  return null;
}

/**
 * Compute a jump score boost based on mode objectives.
 * Returns 0 if no objective or bot shouldn't pursue it.
 */
export function getObjectiveJumpBoost(
  player: PlayerState,
  gameState: GameState,
  skill: number,
  personality: { aggression: number; mobility: number; riskTolerance: number },
): number {
  // Low-skill bots ignore objectives
  if (skill < 0.3) return 0;

  const target = getBotObjectiveTarget(player, gameState);
  if (!target || target.planetIndex === player.planetIndex) return 0;

  // CTF carrier jump restriction
  if (gameState.modeState.ctf
      && gameState.config.modeConfig.carrierCanJump === false
      && gameState.carryables.some(c => c.carriedBy === player.id)) {
    return 0;
  }

  // Carrying an enemy objective? Strong uncapped boost — must get home
  const carrying = gameState.carryables.find(c => c.carriedBy === player.id);
  if (carrying && carrying.teamIndex !== player.teamIndex) {
    return target.urgency * Math.min(1.0, skill * 2); // uncapped for carriers
  }

  // Scale by skill and reduce for aggressive bots (who prefer shooting)
  const boost = target.urgency * Math.min(1.0, skill * 2) * (1 - personality.aggression * 0.4);
  return Math.min(0.5, boost);
}
